import * as THREE from 'three';

// Loads public/textures/papier.<hash>.{webp,jpg} + papier.json once, at
// application startup, and hands the resolved texture to whoever asks.
// Built offline by `npm run build:assets` (tools/build-paper.mjs). Same
// preload/get shape as soundboard-texture.js/crowd-atlas.js: sumi.js reads
// this via options, never fetches it inside init(), so a swap into sumi
// never shows an empty frame while it loads - and per its own spec, sumi
// still renders correctly (a flat 0xf2ede2 paper) with no texture at all.
//
// SAMPLING CONVENTION - the one place it is written down.
//
// configure() below marks the texture SRGBColorSpace, so WebGL uploads it with
// an sRGB internal format and the GPU decodes it on every fetch: what a shader
// receives from texture2D(uPaper, ...) is LINEAR, not the file's own byte
// values. Getting that backwards is not a subtle error - shoji.js calibrated a
// grain curve against the file's sRGB mean of ~0.92 while actually receiving
// ~0.83, which drove the curve past its clamp and pinned every pixel to the
// floor: no grain at all, plus a flat 35% darkening nobody asked for.
//
// So this module also measures the texture and publishes the mean a consumer
// should centre on - see getPaperMean(). Normalising against a measured mean
// rather than a written-down constant is what makes the next paper texture,
// darker or lighter, a non-event.
const MANIFEST_URL = 'textures/papier.json';

let _texture = null;
let _promise = null;
let _mean = 0;

function configure(texture) {
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

function srgbToLinear(c) {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

// Mean of the RED channel in LINEAR light - the same quantity a shader gets
// back from texture2D, so a consumer can subtract it and have the remainder
// centred on zero. Averaged AFTER converting each sample, not before: the
// transfer curve is not linear, so the mean of the decoded values is not the
// decode of the mean.
//
// Returns 0 if the pixels cannot be read at all (a cross-origin canvas taint
// would do it), which consumers must treat as "no measurement" rather than as
// a dark texture - see shoji.js's own guard.
function measureMeanLinear(image) {
  try {
    const w = image.naturalWidth || image.width;
    const h = image.naturalHeight || image.height;
    if (!w || !h) return 0;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return 0;
    ctx.drawImage(image, 0, 0);
    const data = ctx.getImageData(0, 0, w, h).data;
    let sum = 0;
    let n = 0;
    // Every fourth pixel: this texture is deliberately uniform (see
    // tools/build-paper.mjs), so a quarter of a megapixel is far more than
    // enough and keeps startup cost negligible.
    for (let i = 0; i < data.length; i += 16) {
      sum += srgbToLinear(data[i] / 255);
      n++;
    }
    return n > 0 ? sum / n : 0;
  } catch (err) {
    console.warn('[party-mode] paper texture could not be measured', err);
    return 0;
  }
}

function loadTexture(url) {
  return new Promise(function (resolve, reject) {
    new THREE.TextureLoader().load(url, function (t) { resolve(configure(t)); }, undefined, function () {
      reject(new Error(url + ' failed to load'));
    });
  });
}

export function preloadPaperTexture() {
  if (_promise) return _promise;
  _promise = fetch(MANIFEST_URL)
    .then(function (r) {
      if (!r.ok) throw new Error('papier.json HTTP ' + r.status);
      return r.json();
    })
    .then(function (manifest) {
      return loadTexture('textures/' + manifest.webp).catch(function () {
        return loadTexture('textures/' + manifest.jpg);
      });
    })
    .then(function (texture) {
      _texture = texture;
      _mean = texture && texture.image ? measureMeanLinear(texture.image) : 0;
      return _texture;
    })
    .catch(function (err) {
      console.error('[party-mode] paper texture unavailable, sumi will render on a flat paper colour', err);
      _texture = null;
      _mean = 0;
      return null;
    });
  return _promise;
}

export function getPaperTexture() {
  return _texture;
}

// The texture's mean red channel in LINEAR light, or 0 when it could not be
// measured. Centre a grain curve on this rather than on a hard-coded
// reference - see the sampling-convention note at the top.
export function getPaperMean() {
  return _mean;
}
